import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - HIPAA Compliant Healthcare Platform | CheckFirstCare</title>
        <meta name="description" content="CheckFirstCare HIPAA-compliant privacy policy. Learn how we protect your Protected Health Information (PHI) in our patient triage platform and healthcare triage software." />
        <meta name="keywords" content="HIPAA compliance, patient privacy, healthcare data security, Protected Health Information, PHI protection, patient triage platform" />
        
        <meta property="og:title" content="Privacy Policy - HIPAA Compliant Healthcare Platform" />
        <meta property="og:description" content="CheckFirstCare HIPAA-compliant privacy policy. Learn how we protect your Protected Health Information (PHI)." />
        <meta property="og:url" content="https://www.checkfirstcare.com/privacy" />
        <meta property="og:type" content="website" />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/privacy" />
      </Helmet>
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
      <Link 
        to="/" 
        className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Home
      </Link>

      <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
      <p className="text-muted-foreground mb-8">Effective Date: December 1, 2024</p>

      <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          <p className="text-foreground/80 mb-4">
            CheckFirst ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered triage platform designed to reduce non-urgent emergency room visits.
          </p>
          <p className="text-foreground/80">
            CheckFirst was founded by Ranvir Gill, a student at The Haverford School, as part of a social-impact initiative to improve healthcare access and reduce unnecessary ER visits. Through pilot programs, we've successfully reduced ER visits by 39% among participating patients.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
          <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
          <p className="text-foreground/80 mb-4">We may collect personal information that you provide directly to us, including:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Name, email address, and phone number</li>
            <li>Date of birth and demographic information</li>
            <li>Medical symptoms and health information you provide during triage</li>
            <li>Emergency contact information</li>
            <li>Insurance information (if provided)</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Protected Health Information (PHI)</h3>
          <p className="text-foreground/80 mb-4">
            Under HIPAA, we collect and maintain Protected Health Information (PHI) including:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Medical symptoms, conditions, and health concerns you report</li>
            <li>Vital signs and clinical observations</li>
            <li>Triage assessments and recommendations</li>
            <li>Healthcare provider communications related to your care</li>
            <li>Treatment history relevant to your current health concern</li>
          </ul>
          <p className="text-foreground/80 mb-4">
            All PHI is protected under HIPAA regulations and handled with strict confidentiality and security measures as required by federal law.
          </p>

          <h3 className="text-xl font-semibold mb-3">Usage Information</h3>
          <p className="text-foreground/80">
            We automatically collect certain information about your device and how you interact with our platform, including IP address, browser type, pages visited, and time spent on pages. This information is kept separate from your PHI and is used only for platform improvement and security purposes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">How We Use Your Protected Health Information</h2>
          <p className="text-foreground/80 mb-4">
            In accordance with HIPAA Privacy Rule, we use your PHI only for the following permitted purposes:
          </p>
          
          <h3 className="text-xl font-semibold mb-3">Treatment</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Provide AI-powered triage assessments and healthcare guidance</li>
            <li>Connect you with appropriate healthcare resources and facilities</li>
            <li>Coordinate your care with healthcare providers (with your authorization)</li>
            <li>Communicate triage results and follow-up care recommendations</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Healthcare Operations</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Improve our triage algorithms and clinical decision support systems</li>
            <li>Quality assessment and improvement activities</li>
            <li>Training of healthcare professionals and staff</li>
            <li>Conduct outcomes research to reduce unnecessary ER visits</li>
            <li>Customer service and technical support</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Required by Law</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Comply with legal and regulatory requirements</li>
            <li>Respond to court orders and legal proceedings</li>
            <li>Report to public health authorities when required</li>
            <li>Prevent serious threats to health or safety</li>
          </ul>

          <p className="text-foreground/80 mb-4">
            <strong>Important:</strong> We will not use or disclose your PHI for marketing purposes or sell your PHI without your explicit written authorization.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Disclosure of Protected Health Information</h2>
          <p className="text-foreground/80 mb-4">
            <strong>We do not sell your PHI under any circumstances.</strong> We may disclose your PHI only in the following HIPAA-permitted situations:
          </p>
          
          <h3 className="text-xl font-semibold mb-3">With Your Written Authorization</h3>
          <p className="text-foreground/80 mb-4">
            We will obtain your written authorization before using or disclosing your PHI for purposes other than treatment, payment, or healthcare operations, except as permitted or required by law. You may revoke your authorization at any time in writing.
          </p>

          <h3 className="text-xl font-semibold mb-3">To Healthcare Providers</h3>
          <p className="text-foreground/80 mb-4">
            With your authorization or as permitted by HIPAA, we may share triage information with healthcare professionals involved in your care, including emergency departments, primary care physicians, and specialists.
          </p>

          <h3 className="text-xl font-semibold mb-3">Business Associates</h3>
          <p className="text-foreground/80 mb-4">
            We may disclose PHI to Business Associates (service providers) who perform functions on our behalf. All Business Associates must sign HIPAA-compliant Business Associate Agreements ensuring they protect your PHI with the same standards we maintain.
          </p>

          <h3 className="text-xl font-semibold mb-3">As Required or Permitted by Law</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li><strong>Public Health Activities:</strong> Reporting diseases, injuries, or disabilities to public health authorities</li>
            <li><strong>Abuse or Neglect:</strong> Reporting suspected abuse, neglect, or domestic violence as required by law</li>
            <li><strong>Legal Proceedings:</strong> Responding to court orders, subpoenas, or lawful requests</li>
            <li><strong>Law Enforcement:</strong> When required by law or in response to valid legal process</li>
            <li><strong>Health Oversight:</strong> To health oversight agencies for audits, investigations, or inspections</li>
            <li><strong>Serious Threats:</strong> To prevent or lessen serious and imminent threats to health or safety</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">De-identified Information</h3>
          <p className="text-foreground/80">
            We may use and disclose de-identified health information that cannot reasonably be used to identify you for research, quality improvement, and public health purposes. De-identified data is not subject to HIPAA restrictions.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">HIPAA Security Safeguards</h2>
          <p className="text-foreground/80 mb-4">
            We implement comprehensive administrative, physical, and technical safeguards as required by the HIPAA Security Rule to protect your PHI:
          </p>
          
          <h3 className="text-xl font-semibold mb-3">Administrative Safeguards</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Written policies and procedures for PHI handling</li>
            <li>Designated Privacy Officer and Security Officer</li>
            <li>Workforce training on HIPAA compliance and data security</li>
            <li>Access controls limiting PHI access to authorized personnel only</li>
            <li>Regular security risk assessments and audits</li>
            <li>Incident response and breach notification procedures</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Physical Safeguards</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Secure facility access controls</li>
            <li>Workstation and device security measures</li>
            <li>Secure disposal of PHI-containing materials</li>
            <li>Physical access logging and monitoring</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Technical Safeguards</h3>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>End-to-end encryption of PHI in transit and at rest (AES-256 encryption)</li>
            <li>Unique user identification and authentication</li>
            <li>Automatic logoff after periods of inactivity</li>
            <li>Audit controls tracking access to PHI</li>
            <li>Integrity controls to ensure PHI is not improperly altered</li>
            <li>Secure backup and disaster recovery systems</li>
            <li>Network security including firewalls and intrusion detection</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Breach Notification</h3>
          <p className="text-foreground/80">
            In the event of a breach of unsecured PHI, we will notify affected individuals within 60 days as required by HIPAA Breach Notification Rule. We will also notify the Secretary of Health and Human Services and, in certain circumstances, the media.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Your HIPAA Rights</h2>
          <p className="text-foreground/80 mb-4">Under HIPAA, you have the following rights regarding your Protected Health Information:</p>
          
          <h3 className="text-xl font-semibold mb-3">Right to Access</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to inspect and obtain a copy of your PHI maintained in our system. We will respond to your request within 30 days and may charge a reasonable, cost-based fee for copies.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to Amend</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to request that we amend your PHI if you believe it is incorrect or incomplete. We will respond to your request within 60 days.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to an Accounting of Disclosures</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to request an accounting of disclosures of your PHI made by us for purposes other than treatment, payment, or healthcare operations.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to Request Restrictions</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to request restrictions on how we use or disclose your PHI for treatment, payment, or healthcare operations. We are not required to agree to your request but will consider it carefully.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to Request Confidential Communications</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to request that we communicate with you about your health information by alternative means or at alternative locations (e.g., different phone number or address).
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to a Paper Copy of This Notice</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to obtain a paper copy of this Privacy Policy at any time by contacting us.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to Revoke Authorization</h3>
          <p className="text-foreground/80 mb-4">
            You have the right to revoke any authorization you have given us to use or disclose your PHI, except to the extent we have already acted in reliance on your authorization.
          </p>

          <h3 className="text-xl font-semibold mb-3">Right to File a Complaint</h3>
          <p className="text-foreground/80 mb-4">
            If you believe your privacy rights have been violated, you have the right to file a complaint with us or with the Secretary of the Department of Health and Human Services. You will not be retaliated against for filing a complaint.
          </p>
          <p className="text-foreground/80">
            <strong>To file a complaint with HHS:</strong> Office for Civil Rights, U.S. Department of Health and Human Services, 200 Independence Avenue, S.W., Washington, D.C. 20201, or visit www.hhs.gov/ocr/privacy/hipaa/complaints/
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Minors and Parental Rights</h2>
          <p className="text-foreground/80 mb-4">
            For minors under 18, we recognize parental authority and HIPAA requirements regarding minors' PHI:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Parents or legal guardians may access their minor child's PHI as permitted by law</li>
            <li>In some circumstances, minors may have the right to consent to their own care and control their PHI</li>
            <li>We comply with state laws regarding minors' rights to privacy in healthcare</li>
            <li>For children under 13, parental consent is required before using our service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Changes to This Privacy Policy</h2>
          <p className="text-foreground/80">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <p className="text-foreground/80 mb-4">
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-accent/50 p-6 rounded-lg">
            <p className="text-foreground/80 mb-2"><strong>CheckFirst</strong></p>
            <p className="text-foreground/80 mb-2">Founder: Ranvir Gill</p>
            <p className="text-foreground/80 mb-2">Email: ranvgill@haverford.org</p>
            <p className="text-foreground/80">The Haverford School, Haverford, PA</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">HIPAA Compliance Statement</h2>
          <p className="text-foreground/80 mb-4">
            CheckFirst is a HIPAA-covered entity and fully complies with the Health Insurance Portability and Accountability Act of 1996 (HIPAA), including:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li><strong>HIPAA Privacy Rule (45 CFR Part 160 and Part 164, Subparts A and E):</strong> Governs the use and disclosure of PHI</li>
            <li><strong>HIPAA Security Rule (45 CFR Part 164, Subpart C):</strong> Establishes standards for electronic PHI security</li>
            <li><strong>HIPAA Breach Notification Rule (45 CFR Part 164, Subpart D):</strong> Requires notification of PHI breaches</li>
            <li><strong>HITECH Act:</strong> Strengthens HIPAA privacy and security protections</li>
          </ul>
          <p className="text-foreground/80 mb-4">
            This Privacy Policy serves as our Notice of Privacy Practices as required by HIPAA, informing you of our legal duties and privacy practices with respect to your Protected Health Information.
          </p>
          <p className="text-foreground/80">
            We are required by law to maintain the privacy of your PHI, provide you with this notice of our legal duties and privacy practices, and follow the terms of the notice currently in effect.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
          <p className="text-foreground/80">
            We retain your PHI for as long as necessary to fulfill the purposes outlined in this Privacy Policy and as required by applicable laws and regulations. Medical records and PHI are typically retained for a minimum of 6 years from the date of creation or last use, whichever is later, in compliance with HIPAA and state record retention laws.
          </p>
        </section>
      </div>
    </div>
    </>
  );
};

export default PrivacyPage;
